/*
 * The reading pane while nothing is open: the next reply to answer (shape brief "Your next reply",
 * Dawid, 2026-10-19, after design review "Loading and empty", P1). The heading names the job and is
 * real from the first frame. Under it, the reply to answer first (the server's `next` order: Interested
 * and Question first, a "Not a sales reply" nobody doubts last; consult of 2026-10-19) is laid on the
 * paper as a sheet: who and their company, its label, their opening words, how long it has gone
 * unanswered, the agent's word where the person has the agent, and Open, the view's one gold control
 * (Enter does the same while no row is chosen). The next two follow as quiet rows, then the way to
 * all of them with their count, and on a desktop the keys. A workspace with nothing needing a reply
 * keeps the old heading and line; a shortlist that failed says so with Try again. While meeting
 * requests wait, the next reply comes from them, as the rail's work rows put them first (LEA-494),
 * and the way to all of them is "All N in Meeting requests".
 */
import type { ReactNode } from "react";
import { appCount, appDateTime } from "../../src/app-dates.ts";
import type { InboxAgentState } from "../../src/inbox-v2/agent-contract.ts";
import type { InboxConversation } from "../../src/inbox-v2/contract.ts";
import { unansweredTip, waitedLabel } from "../../src/inbox-view.ts";
import { AgentChip } from "../agent/agent-chip.tsx";
import { ClockIcon } from "../icons.tsx";
import { LabelPill } from "./label-key.tsx";
import { leadCompany, personName, rowTime, unansweredTone } from "./list.tsx";
import type { InboxPageModel } from "./use-inbox-page.ts";

type AgentStates = ReadonlyMap<string, InboxAgentState | null>;

/** What the shortlist's `next` order puts first, in the rep's words. */
const NEXT_LINE = "Interested replies and questions first, then the newest.";
/** The same while meeting requests wait: the next reply comes from them (LEA-494). */
const MEETING_LINE = "Leads who asked to meet, the newest first.";

/** Where the next reply comes from, its words, total and the place its link opens. */
function nextFrom(page: InboxPageModel) {
  const counts = page.data.counts.data;
  return page.nextPlace === "meeting"
    ? {
        line: MEETING_LINE,
        total: counts?.meeting,
        name: "Meeting requests",
        choice: { kind: "meeting" } as const,
      }
    : {
        line: NEXT_LINE,
        total: counts?.needs_reply,
        name: "Needs reply",
        choice: { kind: "needs_reply" } as const,
      };
}

/** How long the reply has gone unanswered, in the list's own words and tone; else when it came. */
function NextClock(props: { row: InboxConversation }) {
  const { row } = props;
  const since = row.waitingSince ?? null;
  if (since === null)
    return (
      <time
        className="lcr-i2-next-when"
        dateTime={row.lastMessageAt}
        title={appDateTime(row.lastMessageAt)}
      >
        {rowTime(row.lastMessageAt)}
      </time>
    );
  const now = new Date();
  return (
    <time
      className="lcr-i2-next-when"
      data-tone={unansweredTone(since, now)}
      dateTime={since}
      title={unansweredTip(appDateTime(since))}
    >
      <ClockIcon size={14} />
      Unanswered {waitedLabel(since, now)}
    </time>
  );
}

/**
 * The next reply, as one button: the sheet is the target, so a click anywhere on it opens the
 * conversation, and the gold Open inside it is its label, not a second control.
 */
function NextReply(props: {
  row: InboxConversation;
  agent: InboxAgentState | null;
  showKeys: boolean;
  onOpen: () => void;
}) {
  const { row } = props;
  const company = leadCompany(row);
  return (
    <button type="button" className="lcr-i2-next" onClick={props.onOpen}>
      <span className="lcr-i2-next-head">
        <span className="lcr-i2-next-who" title={row.leadAddress ?? undefined}>
          {personName(row)}
          {company && <span className="lcr-i2-next-company"> · {company}</span>}
        </span>
        {row.label?.code && <LabelPill code={row.label.code} uncertain={row.label.uncertain} />}
      </span>
      {row.snippet && <span className="lcr-i2-next-words">{row.snippet}</span>}
      <span className="lcr-i2-next-foot">
        <NextClock row={row} />
        {props.agent && <AgentChip state={props.agent} className="lcr-i2-next-agent" />}
        <span className="lcr-primary lcr-i2-next-open">
          Open
          {props.showKeys && <kbd className="lcr-kbd">Enter</kbd>}
        </span>
      </span>
    </button>
  );
}

/** The next reply's shape while the shortlist is on its way, shown only after a beat. */
function NextReplyArriving() {
  return (
    <div className="lcr-i2-next lcr-opening" aria-hidden="true">
      <span className="lcr-i2-next-head">
        <span className="lcr-skeleton" data-width="row-lead" />
        <span className="lcr-skeleton" data-width="row-chip" />
      </span>
      <span className="lcr-skeleton" data-width="row-preview" />
      <span className="lcr-i2-next-foot">
        <span className="lcr-skeleton" data-width="row-chip-wide" />
        <span className="lcr-skeleton lcr-i2-next-open-arriving" />
      </span>
    </div>
  );
}

/** What the pane holds above the keys, by what the shortlist has: one return per state. */
function startBody(page: InboxPageModel, agent: AgentStates): ReactNode {
  const { shortlist } = page;
  const [next, ...then] = shortlist.data ?? [];
  const from = nextFrom(page);
  const { total } = from;
  if (next)
    return (
      <>
        <h2>Your next reply</h2>
        <p>{from.line}</p>
        <NextReply
          row={next}
          agent={agent.get(next.id) ?? null}
          showKeys={page.showKeys}
          onOpen={() => page.open(next)}
        />
        {then.length > 0 && (
          <>
            <p className="lcr-i2-start-then" aria-hidden="true">
              Then
            </p>
            <ul className="lcr-i2-start-list" aria-label="More replies that need an answer">
              {then.map((row) => (
                <li key={row.id}>
                  <button type="button" className="lcr-i2-start-row" onClick={() => page.open(row)}>
                    <span className="lcr-i2-start-who">{personName(row)}</span>
                    <span className="lcr-i2-start-gist">{row.snippet}</span>
                    <time dateTime={row.lastMessageAt}>{rowTime(row.lastMessageAt)}</time>
                  </button>
                </li>
              ))}
            </ul>
          </>
        )}
        {/* On that place itself the list beside it is that link already. */}
        {total !== undefined && page.choice.kind !== from.choice.kind && (
          <button
            type="button"
            className="lcr-linklike lcr-i2-start-all"
            onClick={() => page.choose(from.choice)}
          >
            All {appCount(total)} in {from.name}
          </button>
        )}
      </>
    );
  // Waiting on the counts to say where the next reply comes from is waiting on the shortlist too.
  if ((shortlist.isLoading || page.nextPlace === null) && total !== 0)
    return (
      <>
        <h2>Your next reply</h2>
        <p>{from.line}</p>
        <NextReplyArriving />
      </>
    );
  if (shortlist.isError)
    return (
      <>
        <h2>Choose a conversation</h2>
        <p>
          The replies that need an answer did not load.{" "}
          <button type="button" className="lcr-linklike" onClick={() => void shortlist.refetch()}>
            Try again
          </button>
        </p>
      </>
    );
  return (
    <>
      <h2>Choose a conversation</h2>
      <p>The full thread appears here.</p>
    </>
  );
}

export function NothingOpen(props: { page: InboxPageModel; agent: AgentStates }) {
  const { page } = props;
  return (
    <div className="lcr-empty-detail lcr-i2-start">
      {startBody(page, props.agent)}
      {page.showKeys && (
        <p className="lcr-i2-start-keys">
          <kbd className="lcr-kbd">J</kbd> <kbd className="lcr-kbd">K</kbd> move ·{" "}
          <kbd className="lcr-kbd">Enter</kbd> opens · <kbd className="lcr-kbd">/</kbd> search
        </p>
      )}
    </div>
  );
}
